import React, { useCallback, useState, useEffect } from 'react'
import { InputCheck, Loading } from 'src/components'
import { WeightWarningModal } from 'src/components/WeightWarningModal'
import ROUTES from 'src/constants/routes'
import { yupResolver } from '@hookform/resolvers/yup'
import { useNavigate } from 'react-router-dom'
import { useForm, FormProvider, useFormContext } from 'react-hook-form'
import { Button } from 'src/components'
import { useCreateAnimal } from './hooks/useCreateAnimal'
import { AnimalCompleteRegistration } from './components/AnimalCompleteRegistration'
import { AnimalBasicRegistration } from './components/AnimalBasicRegistration'
import { createAnimalSchema, createBasicAnimalSchema, CreateAnimalForm } from 'src/@types/validations/Animal'
import { useAuth } from 'src/contexts/auth'
import { CreateAnimalHeader } from './components/CreateAnimalHeader'
import { useGetBreedList } from 'src/hooks/api/breed/useGetBreedList'
import { useGetLotList } from 'src/hooks/api/lot'
import { useGetPastureList } from 'src/hooks/api/pasture'
import { useGetCoatList } from 'src/hooks/api/coat'
import { useGetBloodGradeList } from 'src/hooks/api/animal'
import { Animal } from 'src/domain/animal/Animal'
import { useStyles } from './styles'
import { useGetBirthCertificateList } from 'src/hooks/api/birth-certificate'
import { EarringDto } from 'src/dto/birth-certificate.dto'
import { useAvailableEarrings } from 'src/hooks/useAvailableEarrings'

interface CreateAnimalProps {
  isCompleteAnimalRegistration: boolean
  setIsCompleteAnimalRegistration: React.Dispatch<React.SetStateAction<boolean>>
}

export interface AnimalRegistrationForm extends Animal {
  earringSelected?: EarringDto
}
export const CreateAnimal = () => {
  const { userFarm: farmId } = useAuth()

  const [isCompleteAnimalRegistration, setIsCompleteAnimalRegistration] = useState(false)

  const formMethods = useForm<CreateAnimalForm>({
    defaultValues: {
      sexo: 'F',
      tipoAnimal: 'nasceu_fazenda',
      idFazenda: farmId!,
      tourosMultiplos: []
    },
    resolver: yupResolver(isCompleteAnimalRegistration ? createAnimalSchema : createBasicAnimalSchema)
  })

  return (
    <FormProvider {...formMethods}>
      <CreateAnimalComponent
        isCompleteAnimalRegistration={isCompleteAnimalRegistration}
        setIsCompleteAnimalRegistration={setIsCompleteAnimalRegistration}
      />
    </FormProvider>
  )
}

export const CreateAnimalComponent: React.FC<CreateAnimalProps> = ({
  isCompleteAnimalRegistration,
  setIsCompleteAnimalRegistration
}) => {
  const { username } = useAuth()
  const { reset, setValue } = useFormContext<AnimalRegistrationForm>()

  const navigate = useNavigate()

  const {
    createContinuosAnimal,
    onSubmit,
    setAnimalImage,
    handleSaveImage,
    setCreateContinuosAnimal,
    showWeightWarning,
    weightValidationResult,
    handleConfirmWeightWarning,
    handleCancelWeightWarning,
    isCreatingAnimal
  } = useCreateAnimal()

  const styles = useStyles()

  const handleChangeAnimalRegistration = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      reset()
      setIsCompleteAnimalRegistration(event.target.checked)
    },
    [reset]
  )

  const { data: breedList, isLoading: isLoadingBreedListing } = useGetBreedList()
  const { data: lotsList, isLoading: isLoadingLotListing } = useGetLotList()
  const { data: pastureList, isLoading: isLoadingPastureListing } = useGetPastureList()
  const { data: coatList, isLoading: isLoadingCoatListing } = useGetCoatList()
  const { data: bloodGradeList, isLoading: isLoadingBloodGradeListing } = useGetBloodGradeList()

  const { data: birthCertificateList, isLoading: isBirthCertificateListLoading } = useGetBirthCertificateList({
    userFilter: username
  })

  const { selectFirstEarring } = useAvailableEarrings({
    birthCertificateList
  })

  useEffect(() => {
    const earring = selectFirstEarring()
    if (earring) {
      setValue('earringSelected', earring)
      setValue('rgn', earring.numeroSufixo)
      setValue('cdn', earring.numeroCompleto)
    }
  }, [birthCertificateList, isCompleteAnimalRegistration, selectFirstEarring, setValue])

  const isLoading =
    isLoadingBloodGradeListing ||
    isLoadingBreedListing ||
    isLoadingLotListing ||
    isLoadingPastureListing ||
    isLoadingCoatListing ||
    isBirthCertificateListLoading

  const renderForm = () => {
    switch (isCompleteAnimalRegistration) {
      case true:
        return (
          <AnimalCompleteRegistration
            breedList={breedList || []}
            lotsList={lotsList || []}
            pastureList={pastureList || []}
            coatList={coatList || []}
            bloodGradeList={bloodGradeList || []}
            isDiagnosticRestiger={false}
            setAnimalImage={setAnimalImage}
            handleSaveImage={handleSaveImage}
          />
        )

      case false:
        return <AnimalBasicRegistration setAnimalImage={setAnimalImage} handleSaveImage={handleSaveImage} />

      default:
        return null
    }
  }

  return (
    <>
      <CreateAnimalHeader
        isCompleteAnimalRegistration={isCompleteAnimalRegistration}
        onChange={handleChangeAnimalRegistration}
        showSwitch={!isLoading}
      />

      {isLoading ? (
        <Loading />
      ) : (
        <>
          <form onSubmit={onSubmit}>
            {renderForm()}
            <div className={styles.footerButtonContainer}>
              <InputCheck
                id='createContinuosAnimal'
                value='create'
                type='checkbox'
                label='Criar outro cadastro'
                name='createContinuosAnimal'
                checked={createContinuosAnimal}
                onChange={(e) => setCreateContinuosAnimal(e.target.checked)}
              />

              <div className={styles.buttonContainer}>
                <Button
                  type='button'
                  className={`${styles.buttonOutlined} ${styles.buttonWrapper}`}
                  outlined
                  onClick={() => navigate(ROUTES.ANIMAL.ROOT)}
                >
                  Cancelar
                </Button>

                <Button
                  className={styles.buttonWrapper}
                  type='submit'
                  isLoading={isCreatingAnimal}
                  disabled={isCreatingAnimal}
                >
                  Salvar
                </Button>
              </div>
            </div>
          </form>

          <WeightWarningModal
            isLoading={isCreatingAnimal}
            open={showWeightWarning}
            validationResult={weightValidationResult}
            onConfirm={handleConfirmWeightWarning}
            onCancel={handleCancelWeightWarning}
          />
        </>
      )}
    </>
  )
}
